/* Lunafreya Maison header: scroll states, mega menu, menu sheet, predictive search and the bag drawer.
   Other sections open the bag through any of: [data-luna-header-v25]._lunafreya.openPanel('cart'),
   a click on .luna-root [data-luna-open="cart"], or [data-cart-drawer].open(); and refresh it with the
   lunafreya:cart-updated / cart:updated / cart:refresh / cart:update events. */
(() => {
  const root = document.querySelector('[data-mh]');
  if (!root || root._lunafreya) return;

  const $ = (sel, scope = root) => scope.querySelector(sel);
  const $$ = (sel, scope = root) => [...scope.querySelectorAll(sel)];
  const fixed = $('[data-mh-fixed]');
  const scrim = $('[data-mh-scrim]');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const WISH_KEY = `lunafreya:wishlist:${location.host}`;

  /* ---------- Money ---------- */
  const moneyFormat = (root.dataset.moneyFormat || '₹{{amount}}').replace(/Rs\.?\s*/, '₹');
  const group = (n, dec, thousands = ',', point = '.') => {
    const [int, frac] = (n / 100).toFixed(dec).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
  };
  const money = (cents) => moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    if (key === 'amount_no_decimals') return group(cents, 0);
    if (key === 'amount_with_comma_separator') return group(cents, 2, '.', ',');
    if (key === 'amount_no_decimals_with_comma_separator') return group(cents, 0, '.');
    return group(cents, 2);
  }).replace(/\.00$/, '');
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const sized = (url, w) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${w}` : '');

  /* ---------- Former panels ----------
     With "Use the former menu and search" on, the Lunafreya Glass Header section stays enabled with its bar
     hidden, and this bar opens its menu, search and phone menu through its own API. */
  const useFormer = root.dataset.former === 'true';
  const formerRoot = () => document.querySelector('[data-luna-header-v25]:not([data-mh])');
  const formerApi = () => formerRoot()?._lunafreya || null;
  const formerOpen = () => useFormer && !!formerApi()?.openPanelName;
  // Older sections open the bag through [data-luna-header-v25]._lunafreya, which is now the former header:
  // send its "cart" to this bag so there is only ever one bag drawer.
  // The bar matches the search panel while it is open; the panel can also close itself (its × or Esc).
  const syncSearch = () => fixed.classList.toggle('is-search', !!formerRoot()?.classList.contains('has-open-panel'));
  const patchFormer = () => {
    const api = formerApi();
    if (!api) return false;
    if (api.__maison) return true;
    new MutationObserver(syncSearch).observe(formerRoot(), { attributes: true, attributeFilter: ['class'] });
    syncSearch();
    // Search suggestions: only what the house sells.
    const terms = (root.dataset.popular || '').split(',').map((term) => term.trim()).filter(Boolean);
    const list = formerRoot().querySelector('[data-luna-popular-list]');
    if (list && terms.length) list.innerHTML = terms.map((term) => `<button type="button" data-luna-popular-query="${esc(term.toLowerCase())}"><span>${esc(term)}</span></button>`).join('');
    const original = api.openPanel.bind(api);
    api.openPanel = (name, trigger) => (name === 'cart' ? openPanel('cart', trigger) : original(name, trigger));
    // Search: internal pages are not articles, and pages are labelled as pages.
    const render = api.renderSearchResults;
    if (render) {
      api.renderSearchResults = (groups, query) => render(groups && groups.pages
        ? { ...groups, pages: groups.pages.filter((page) => !/\/pages\/(wishlist|lunafreya-menu)\b/.test(page.url || '')) }
        : groups, query);
    }
    const card = api.searchContentCard.bind(api);
    api.searchContentCard = (item, label, query) => card(item, /\/pages\//.test(item.url || '') ? 'Page' : label, query);
    api.__maison = true;
    return true;
  };
  const openFormer = (name, trigger) => {
    const api = useFormer && formerApi();
    if (!api) return false;
    if (api.openPanelName === name) { api.closePanels(); return true; }
    fixed.classList.remove('is-hidden');
    setHeight();
    api.openPanel(name, trigger);
    return true;
  };

  /* ---------- Scroll: solid after the first pixels, hidden while reading down ----------
     Horizon scrolls .page-wrapper on wide screens and the window on narrow ones, so read whichever moves. */
  const scrollY = () => {
    const pw = document.querySelector('.page-wrapper');
    return pw && pw.scrollHeight > pw.clientHeight + 1 && getComputedStyle(pw).overflowY !== 'visible' ? pw.scrollTop : window.scrollY;
  };
  let lastY = scrollY();
  let ticking = false;
  const hideOnScroll = root.dataset.hideOnScroll === 'true';
  const onScroll = () => {
    const y = scrollY();
    fixed.classList.toggle('is-scrolled', y > 8);
    if (hideOnScroll && !root.classList.contains('has-panel') && !fixed.classList.contains('is-open') && !formerOpen()) {
      if (y > lastY + 4 && y > 240) fixed.classList.add('is-hidden');
      else if (y < lastY - 4 || y < 240) fixed.classList.remove('is-hidden');
    }
    lastY = y;
    ticking = false;
  };
  document.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true, capture: true });
  onScroll();

  // --mh-bottom places the former header's panels just under this bar (see maison-header.css).
  const setHeight = () => {
    document.body.style.setProperty('--header-height', `${fixed.offsetHeight}px`);
    document.documentElement.style.setProperty('--mh-bottom', `${fixed.classList.contains('is-hidden') ? 0 : fixed.offsetHeight}px`);
  };
  setHeight();
  window.addEventListener('resize', setHeight);
  fixed.addEventListener('transitionend', setHeight);
  new ResizeObserver(setHeight).observe(fixed);

  /* ---------- Menu: words, then tiles ---------- */
  const narrow = window.matchMedia('(max-width: 989px)');
  const hoverable = window.matchMedia('(hover: hover)');
  const setSub = (id) => {
    $$('[data-mn-sub]').forEach((panel) => panel.classList.toggle('is-open', panel.dataset.mnSub === id));
    $$('[data-mn-row]').forEach((row) => {
      const on = row.dataset.mnRow === id;
      row.classList.toggle('is-active', on);
      row.querySelector('[data-mn-open]')?.setAttribute('aria-expanded', String(on));
    });
    $('.mn')?.classList.toggle('has-sub', !!id);
  };
  let subTimer;
  $$('.mn-row').forEach((row) => {
    row.addEventListener('mouseenter', () => {
      if (narrow.matches || !hoverable.matches) return;
      clearTimeout(subTimer);
      subTimer = setTimeout(() => setSub(row.dataset.mnRow || null), 110);
    });
    row.addEventListener('mouseleave', () => clearTimeout(subTimer));
  });

  /* ---------- Sheets: menu, search, cart ---------- */
  let current = null;
  let returnFocus = null;
  const panelOf = (name) => $(`[data-mh-panel="${name}"]`);
  const openPanel = (name, trigger) => {
    const panel = panelOf(name);
    if (!panel) return;
    if (current && current !== name) closePanel(false);
    returnFocus = trigger || document.activeElement;
    current = name;
    if (name === 'menu') { fixed.classList.add('is-open'); document.documentElement.setAttribute('data-mn-active', ''); setHeight(); }
    scrim.hidden = false;
    void scrim.offsetWidth; // commit the starting state so the slide runs, even in a background tab
    scrim.classList.add('is-open');
    panel.classList.add('is-open');
    root.classList.add('has-panel');
    fixed.classList.remove('is-hidden');
    document.body.classList.add('mh-locked'); document.documentElement.setAttribute('scroll-lock', '');
    $$(`[data-mh-open="${name}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    if (name === 'cart') renderCart();
    if (name === 'search') setTimeout(() => $('[data-mh-q]')?.focus({ preventScroll: true }), reduce ? 0 : 320);
    else setTimeout(() => panel.querySelector('button, a, input')?.focus({ preventScroll: true }), reduce ? 0 : 320);
  };
  const closePanel = (restore = true) => {
    if (!current) return;
    const panel = panelOf(current);
    if (current === 'menu') { fixed.classList.remove('is-open'); document.documentElement.removeAttribute('data-mn-active'); setSub(null); }
    panel?.classList.remove('is-open');
    $$(`[data-mh-open="${current}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    scrim.classList.remove('is-open');
    setTimeout(() => { if (!current) scrim.hidden = true; }, 450);
    root.classList.remove('has-panel');
    document.body.classList.remove('mh-locked'); document.documentElement.removeAttribute('scroll-lock');
    current = null;
    if (restore && returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
  };
  root.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-mh-open]');
    if (opener && opener.dataset.mhOpen === 'search' && openFormer('search', opener)) { e.preventDefault(); closePanel(false); return; }
    if (opener) { e.preventDefault(); current === opener.dataset.mhOpen ? closePanel() : openPanel(opener.dataset.mhOpen, opener); return; }
    if (e.target.closest('[data-mh-close]') || e.target === scrim) { closePanel(); return; }
    const moreBtn = e.target.closest('[data-mn-open]');
    if (moreBtn) { setSub(moreBtn.getAttribute('aria-expanded') === 'true' && !narrow.matches ? null : moreBtn.dataset.mnOpen); return; }
    if (e.target.closest('[data-mn-back]')) { setSub(null); return; }
    if (e.target.closest('[data-mn-newsletter]')) {
      closePanel(false);
      const letter = document.getElementById('MaisonLetterEmail');
      if (letter) { letter.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' }); setTimeout(() => letter.focus({ preventScroll: true }), reduce ? 0 : 500); }
      return;
    }
    // Touch screens and small windows: the word opens its tiles; "View all" is the first tile.
    const rowLink = e.target.closest('.mn-link');
    const row = rowLink && rowLink.closest('[data-mn-row]');
    if (row && (narrow.matches || !hoverable.matches)) { e.preventDefault(); setSub(row.dataset.mnRow); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { if (current) closePanel(); }
    if (e.key === 'Tab' && current) {
      // Keep keyboard focus inside the open sheet.
      const items = $$('a[href], button:not([disabled]), input:not([type="hidden"])', panelOf(current)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Search ---------- */
  const q = $('[data-mh-q]');
  const results = $('[data-mh-results]');
  const popular = $('[data-mh-popular]');
  let searchTimer;
  let searchCtrl;
  const runSearch = async (term) => {
    searchCtrl?.abort();
    if (term.length < 2) { results.innerHTML = ''; if (popular) popular.hidden = false; return; }
    searchCtrl = new AbortController();
    const url = `${root.dataset.suggestUrl}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
    try {
      const data = await (await fetch(url, { signal: searchCtrl.signal })).json();
      const products = data?.resources?.results?.products || [];
      if (popular) popular.hidden = true;
      const all = `${root.dataset.searchUrl}?type=product&q=${encodeURIComponent(term)}`;
      results.innerHTML = products.length
        ? `<p class="mh-eyebrow">Pieces</p><div class="mh-results">${products.map((p) => `
            <a class="mh-result" href="${esc(p.url)}">
              <div class="mh-result__img">${p.image ? `<img src="${esc(sized(p.image, 400))}" alt="" loading="lazy">` : ''}</div>
              <p class="mh-result__name">${esc(p.title)}</p>
              <p class="mh-result__price">${money(Math.round(parseFloat(p.price) * 100))}</p>
            </a>`).join('')}</div><a class="mh-search__all" href="${esc(all)}">View all results</a>`
        : `<p class="mh-search__empty">Nothing found for “${esc(term)}”. Try another word, or <a href="${esc(all)}" style="border-bottom:1px solid">search everything</a>.</p>`;
    } catch (err) { if (err.name !== 'AbortError') results.innerHTML = ''; }
  };
  q?.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => runSearch(q.value.trim()), 220); });

  /* ---------- Bag ---------- */
  const lines = $('[data-mh-lines]');
  const empty = $('[data-mh-empty]');
  const foot = $('[data-mh-foot]');
  const threshold = Number(root.dataset.threshold) || 0;
  let cartState = null;

  const setCount = (n) => {
    document.querySelectorAll('[data-mh-count]').forEach((el) => { el.textContent = n; });
    $('.mh-act--bag [data-mh-count]')?.setAttribute('aria-label', `${n} item${n === 1 ? '' : 's'} in bag`);
  };
  // The charge for initials is a line of its own, tied to its bag by _Pair. It is shown with the bag, never as a piece.
  const isFee = (item) => item.handle === 'personalisation';
  const pieces = (cart) => cart.items.reduce((sum, item) => (isFee(item) ? sum : sum + item.quantity), 0);
  const feeFor = (cart, item) => (item.properties?._Pair ? cart.items.find((other) => isFee(other) && other.properties?._Pair === item.properties._Pair) : null);
  const hasBag = (cart, fee) => cart.items.some((other) => !isFee(other) && other.properties?._Pair && other.properties._Pair === fee.properties?._Pair);
  const fetchCart = async () => {
    const cart = await (await fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
    cartState = cart;
    setCount(pieces(cart));
    return cart;
  };
  const drawCart = (cart) => {
    const has = cart.item_count > 0;
    empty.hidden = has;
    foot.hidden = !has;
    if (!has) drawRecs();
    lines.innerHTML = cart.items.map((item, i) => {
      if (isFee(item) && hasBag(cart, item)) return '';
      const fee = isFee(item) ? null : feeFor(cart, item);
      const said = item.properties?.Name ? `${item.properties.Name}${item.properties.Lettering ? ` in ${item.properties.Lettering}` : ''}` : '';
      const opts = (item.options_with_values || []).filter((o) => o.name !== 'Title' && o.value !== 'Default Title').map((o) => o.value).join(' · ');
      const was = item.original_line_price > item.final_line_price ? `<s>${money(item.original_line_price)}</s>` : '';
      return `<div class="mh-line" data-line="${i + 1}"${fee ? ` data-key="${esc(item.key)}" data-fee-key="${esc(fee.key)}"` : ''}>
        <a class="mh-line__img" href="${esc(item.url)}">${item.image ? `<img src="${esc(sized(item.image, 300))}" alt="" loading="lazy">` : ''}</a>
        <div>
          ${fee ? '<p class="m-made-for">Personalised</p>' : ''}
          <p class="mh-line__name"><a href="${esc(item.url)}">${esc(item.product_title)}</a></p>
          ${opts ? `<p class="mh-line__meta">${esc(opts)}</p>` : ''}
          ${said ? `<p class="mh-line__meta">Name: ${esc(said)}</p>` : ''}
          ${item.properties?.Side ? `<p class="mh-line__meta">Side: ${esc(item.properties.Side)}</p>` : ''}
          ${fee && item.properties['_See placement'] ? `<p class="mh-line__meta"><a class="m-made-edit" href="${esc(item.properties['_See placement'])}&edit=${encodeURIComponent(item.properties._Pair)}">Edit personalisation</a></p>` : ''}
          ${fee ? `<p class="mh-line__meta">Bag: ${money(item.final_line_price)}</p><p class="mh-line__meta">Personalisation: +${money(fee.final_line_price)}</p>` : ''}
          <div class="mh-line__row">
            <div class="mh-qty" role="group" aria-label="Quantity">
              <button type="button" data-qty="${item.quantity - 1}" aria-label="One fewer">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-qty="${item.quantity + 1}" aria-label="One more">+</button>
            </div>
            <span class="mh-line__price">${was}${money(item.final_line_price + (fee ? fee.final_line_price : 0))}</span>
          </div>
          <button type="button" class="mh-remove" data-qty="0">Remove</button>
        </div>
      </div>`;
    }).join('');
    $('[data-mh-subtotal]').textContent = money(cart.total_price);
    const progress = $('[data-mh-progress]');
    if (progress && threshold > 0) {
      progress.hidden = !has;
      const left = threshold - cart.total_price;
      $('[data-mh-progress-text]').textContent = left > 0 ? `${money(left)} away from complimentary delivery` : 'Your order ships complimentary';
      $('[data-mh-progress-bar]').style.transform = `scaleX(${Math.min(1, cart.total_price / threshold)})`;
    }
  };
  const renderCart = async () => { try { drawCart(await fetchCart()); } catch (e) { /* the bag page still works */ } };
  lines.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const line = btn.closest('[data-line]');
    line.classList.add('is-busy');
    try {
      const quantity = Math.max(0, Number(btn.dataset.qty));
      const paired = line.dataset.feeKey;
      const res = await fetch(`${root.dataset.cartUrl}/${paired ? 'update' : 'change'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(paired ? { updates: { [line.dataset.key]: quantity, [line.dataset.feeKey]: quantity } } : { line: Number(line.dataset.line), quantity })
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || 'Could not update');
      cartState = cart;
      setCount(pieces(cart));
      drawCart(cart);
      document.dispatchEvent(new CustomEvent('maison:cart-changed', { detail: cart }));
    } catch (err) {
      line.classList.remove('is-busy');
      const p = document.createElement('p');
      p.className = 'mh-line__meta';
      p.textContent = err.message;
      line.lastElementChild.append(p);
    }
  });
  ['lunafreya:cart-updated', 'cart:updated', 'cart:refresh', 'cart:update', 'shopify:cart:lines-update'].forEach((name) => {
    document.addEventListener(name, () => (current === 'cart' ? renderCart() : fetchCart().catch(() => {})));
  });

  /* ---------- Empty bag: what was just looked at, then suggestions ---------- */
  // Not lunafreya:recent-products: the former header's bag code writes that key, without prices.
  const RECENT_KEY = `maison:recently-viewed:${location.host}`;
  const recentItems = () => { try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => x && x.handle) : []; } catch (e) { return []; } };
  const viewing = $('[data-mh-viewing]');
  if (viewing) {
    try {
      const item = JSON.parse(viewing.textContent);
      localStorage.setItem(RECENT_KEY, JSON.stringify([item, ...recentItems().filter((x) => x.handle !== item.handle)].slice(0, 8)));
    } catch (e) { /* no storage: the bag simply shows suggestions */ }
  }
  const drawRecs = () => {
    const box = $('[data-mh-recent]');
    const current = viewing ? (() => { try { return JSON.parse(viewing.textContent).handle; } catch (e) { return ''; } })() : '';
    const recent = box ? recentItems().filter((x) => x.handle !== current).slice(0, 4) : [];
    if (box) {
      box.hidden = !recent.length;
      $('[data-mh-recent-grid]').innerHTML = recent.map((p) => `
        <article class="mh-rec" data-handle="${esc(p.handle)}">
          <a class="mh-rec__media" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : ''}</a>
          <h3 class="mh-rec__name"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
          <p class="mh-rec__price">${esc(p.price)}</p>
          ${p.variant ? `<button type="button" class="mh-rec__add" data-mh-add="${Number(p.variant)}" aria-label="Add ${esc(p.title)} to bag">Add to bag</button>` : `<a class="mh-rec__add" href="${esc(p.url)}">View piece</a>`}
        </article>`).join('');
    }
    // Suggestions never repeat what is shown above, and stop at four.
    const seen = new Set([...recent.map((x) => x.handle), current]);
    let shown = 0;
    $$('[data-mh-suggest] .mh-rec').forEach((card) => {
      const show = !seen.has(card.dataset.handle) && shown < 4;
      card.hidden = !show;
      if (show) shown += 1;
    });
    const suggest = $('[data-mh-suggest]');
    if (suggest) suggest.hidden = shown === 0;
  };
  empty.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-mh-add]');
    if (!btn) return;
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Adding';
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.mhAdd), quantity: 1 }] })
      });
      if (!res.ok) throw new Error('add failed');
      await renderCart();
    } catch (err) {
      btn.textContent = 'Unavailable';
      setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 1800);
    }
  });

  /* ---------- Wishlist: same storage key as the rest of the theme ---------- */
  const wishItems = () => { try { const v = JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => x && x.handle) : []; } catch (e) { return []; } };
  const wishSave = (items) => { try { localStorage.setItem(WISH_KEY, JSON.stringify(items.slice(0, 100))); } catch (e) {} window.dispatchEvent(new CustomEvent('lunafreya:wishlist:changed', { detail: { items } })); };
  const wishCount = () => {
    const n = wishItems().length;
    $$('[data-mh-wish-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
  };
  wishCount();
  window.addEventListener('lunafreya:wishlist:changed', wishCount);
  window.addEventListener('lunafreya:wishlist-change', wishCount);
  window.addEventListener('storage', (e) => { if (e.key === WISH_KEY) wishCount(); });
  const wishlist = {
    items: wishItems,
    has: (handle) => wishItems().some((x) => x.handle === handle),
    add(product) { if (!product?.handle || this.has(product.handle)) return; wishSave([{ ...product, addedAt: Date.now() }, ...wishItems()]); },
    remove(handle) { wishSave(wishItems().filter((x) => x.handle !== handle)); },
    toggle(product) { if (this.has(product.handle)) { this.remove(product.handle); return false; } this.add(product); return true; }
  };
  if (!window.LunafreyaWishlist) window.LunafreyaWishlist = wishlist;
  window.MaisonWishlist = wishlist;

  /* ---------- Cut-out photographs: shown whole, never cropped ----------
     Product photos are a mix of transparent cut-outs and full-frame studio shots; only the pixels can tell. */
  const FIT = '.msr-card__a, .msr-shape__img img, .msh-row__img img, .mrv-card__media img, .erg-slide img, .m-trend__media img, .m-card__media img, .mh-rec__media img, .m-cine__card-img img, .mc-item__img img, .mc-rec__img img, .wl-media img, .lj-piece__img img, .lj-plate img, .lj-frame__img img, .lj-fig img, .luna-search-content-card__media img';
  const alphaCache = new Map();
  // cutout: transparent corners. stray: a sliver of opaque pixels down the very edge, left by the cut-out tool.
  const inspect = (src) => {
    const key = src.split('?')[0];
    if (!alphaCache.has(key)) {
      alphaCache.set(key, new Promise((resolve) => {
        const no = { cutout: false, stray: false };
        if (!/\.png/i.test(key)) { resolve(no); return; }
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
          try {
            const w = probe.naturalWidth;
            const h = probe.naturalHeight;
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            const x = c.getContext('2d', { willReadFrequently: true });
            x.drawImage(probe, 0, 0);
            const cutout = x.getImageData(0, 0, 1, 1).data[3] < 40 && x.getImageData(w - 1, 0, 1, 1).data[3] < 40;
            const tall = (col) => { const d = x.getImageData(col, 0, 2, h).data; let n = 0; for (let i = 3; i < d.length; i += 4) if (d[i] > 8) n += 1; return n > h; };
            resolve({ cutout, stray: cutout && (tall(0) || tall(w - 2)), ratio: w / h });
          } catch (e) { resolve(no); }
        };
        probe.onerror = () => resolve(no);
        probe.src = `${key}?width=300`;
      }));
    }
    return alphaCache.get(key);
  };
  const isCutout = (src) => inspect(src).then((info) => info.cutout);
  // The image CDN crops a sliver off each side, so the mark never shows and the product file stays as it is.
  const trimmed = (src, ratio) => `${src.split('?')[0]}?width=900&height=${Math.round(900 / (ratio * 0.96))}&crop=center`;
  const markCutouts = () => document.querySelectorAll(FIT).forEach((img) => {
    if (img.dataset.mFit) return;
    const src = img.currentSrc || img.getAttribute('src') || (img.getAttribute('srcset') || '').split(' ')[0];
    if (!src || src.startsWith('data:')) return;
    img.dataset.mFit = '1';
    inspect(src).then(({ cutout, stray, ratio }) => {
      if (cutout) img.classList.add('m-cutout');
      if (stray) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = trimmed(src, ratio); }
    });
  });
  /* ---------- Words: on hover the word rolls up and returns in the wordmark's italic. The copy is drawn from an
     attribute, so reading a button's text still gives one word. Rebuilt text is wrapped again on the next pass. ---------- */
  const ROLL = '.mh-link, .mh-act .mh-txt, .mh-menu-btn .mh-txt, .mh-head-acts a, .mh-remove, .mc-remove, .wl-remove, .wl-link, .erg-filter-clear, .mh-rec__add, .lfp__recommend-add, .lfq-copy, .mf-col__list a';
  const rollWords = () => document.querySelectorAll(ROLL).forEach((host) => {
    if (host.querySelector('.m-roll')) return;
    const node = [...host.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
    if (!node) return;
    const text = node.textContent.trim();
    const roll = document.createElement('span');
    roll.className = 'm-roll';
    const first = document.createElement('span');
    first.className = 'm-roll__a';
    first.textContent = text;
    const second = document.createElement('span');
    second.className = 'm-roll__b';
    second.dataset.text = text;
    second.setAttribute('aria-hidden', 'true');
    roll.append(first, second);
    host.classList.add('m-roll-host');
    node.replaceWith(roll);
  });
  rollWords();

  /* ---------- Personalised bags: said plainly beside the initials box, before anything is paid ---------- */
  document.querySelectorAll('.lfp__personalize-input-wrap').forEach((wrap) => {
    if (!wrap.querySelector('input[name="properties[Name]"]') || wrap.querySelector('.m-personal-note')) return;
    const note = document.createElement('p');
    note.className = 'm-personal-note';
    note.textContent = 'Personalised pieces are made for you alone and can only be returned if faulty or incorrect. Please check the spelling.';
    wrap.append(note);
  });

  let fitTimer;
  new MutationObserver(() => { clearTimeout(fitTimer); fitTimer = setTimeout(() => { markCutouts(); rollWords(); }, 250); }).observe(document.documentElement, { childList: true, subtree: true });
  markCutouts();

  /* ---------- Search: article cards carry the entry's number, a few lines and a way in ---------- */
  const journalIndex = (() => {
    try { return JSON.parse(document.querySelector('[data-m-journal-index]').textContent); } catch (e) { return {}; }
  })();
  const dress = (card) => {
    if (card.querySelector('.ms-read')) return;
    const copy = card.children[1];
    if (!copy) return;
    const span = (cls, text) => {
      const node = document.createElement('span');
      node.className = cls;
      if (text != null) node.textContent = text;
      return node;
    };
    const info = journalIndex[new URL(card.href, location.href).pathname];
    if (info) {
      card.classList.add('ms-entry');
      card.children[0].append(span('ms-tag', `Entry ${String(info.n).padStart(2, '0')}`));
      const label = copy.querySelector('small');
      if (label) label.after(span('ms-date', info.d));
      copy.append(span('ms-text', info.e));
    } else {
      card.classList.add('ms-page');
    }
    const foot = span('ms-foot');
    foot.append(span('ms-read', 'Read more'));
    if (info) foot.append(span('ms-mins', `${info.m} min read`));
    copy.append(foot);
  };
  const dressCards = () => document.querySelectorAll('.luna-search-content-card[href*="/blogs/"], .luna-search-content-card[href*="/pages/"]').forEach(dress);
  const searchPanel = document.querySelector('.luna-panel--search');
  if (searchPanel) {
    new MutationObserver(dressCards).observe(searchPanel, { childList: true, subtree: true });
    dressCards();
  }

  /* ---------- Hero card and trending: never a studio rectangle inside a tile ----------
     A picture or hover picture set in the editor is used as it is. Otherwise the product's own transparent
     cut-outs are used; with none, a studio shot fills the frame. With no second cut-out, hovering only zooms. */
  const fileName = (url) => url.split('?')[0].split('/').pop();
  const sameFile = (a, b) => fileName(a) === fileName(b);
  document.querySelectorAll('[data-m-photos]').forEach(async (box) => {
    const main = box.querySelector('img:not(.is-hover)');
    if (!main) return;
    const photos = box.dataset.mPhotos.split('|').filter(Boolean).slice(0, 10);
    const flags = await Promise.all(photos.map(isCutout));
    const cutouts = photos.filter((_, i) => flags[i]);
    const src = () => main.getAttribute('src') || main.currentSrc;
    if (!box.hasAttribute('data-m-own-image') && !(await isCutout(src()))) {
      if (cutouts[0]) { main.removeAttribute('srcset'); main.src = cutouts[0]; main.dataset.mFit = ''; main.classList.add('m-cutout'); markCutouts(); }
      else main.classList.add('is-photo');
    }
    if (box.querySelector('.is-hover') || !window.matchMedia('(hover: hover)').matches) return;
    const next = cutouts.find((url) => !sameFile(url, src()));
    if (!next) return;
    const hover = new Image();
    hover.className = 'is-hover m-cutout';
    hover.alt = '';
    hover.loading = 'lazy';
    hover.src = next;
    box.append(hover);
  });

  /* ---------- Public API for other sections ---------- */
  const api = { openPanel, closePanel, renderCart, refresh: renderCart, getWishlist: wishItems, addWishlist: (p) => wishlist.add(p), removeWishlist: (h) => wishlist.remove(h) };
  root._lunafreya = api;
  let tries = 0;
  const waitForFormer = () => { if (!patchFormer() && tries++ < 40) setTimeout(waitForFormer, 250); };
  waitForFormer();
  const drawer = panelOf('cart');
  drawer.open = () => openPanel('cart');
  drawer.close = () => closePanel();
  window.MaisonHeader = api;
})();
